/**
 * HeightSafetyQuoteRequestSend · iter303ak-fix26 — Quote Sent · T25 · A50
 * ─────────────────────────────────────────────────────────────────────
 * MOCKED per-supplier send. Reads selection from prev action, per-row
 * Send button + Send-all. Stamps `height_safety_quote_requested_at.{sid}`
 * for tracking. Actual SMTP deferred to real M365 creds — response is
 * logged to the audit trail.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { sendWorkflowEmail, jobRef, signOff } from './_emailSend';
import { Mail, Send, CheckCircle2, AlertCircle } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export default function HeightSafetyQuoteRequestSend({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const parentAppraisalId = capturedData?.appraisal_id;
  const stampFieldPrefix = params.stamp_field_prefix || 'height_safety_quote_requested_at';

  const selection = useMemo(
    () => capturedData?.height_safety_supplier_selection?.selection
      || capturedData?.height_safety_supplier_selection
      || {},
    [capturedData]
  );

  const items = useMemo(
    () => Object.entries(selection)
      .filter(([, v]) => v && v.include)
      .map(([sid, v]) => ({ supplier_id: sid, name: v.name, email: v.email })),
    [selection]
  );

  const [sendState, setSendState] = useState(() => value?.send_state || {});
  const [busy, setBusy] = useState(() => new Set());
  const [globalErr, setGlobalErr] = useState('');

  const isSent = (sid) => !!sendState[sid]?.sent_at;

  const sendOne = async (sid, email) => {
    if (isSent(sid)) return;
    setBusy(prev => { const n = new Set(prev); n.add(sid); return n; });
    setGlobalErr('');
    try {
      const it = items.find(x => x.supplier_id === sid) || {};
      await sendWorkflowEmail(api, {
        to: email,
        subject: 'Height safety quote request – Roofs To Remedy Ltd',
        body: `Hi ${it.name || ''},\n\nPlease find our height safety quote request attached. Reply with your quote and availability.${signOff}`,
        attachments: [{ route: `/reports/status-letter/height_safety_quote_request/${parentAppraisalId}`, filename: 'Height_Safety_Quote_Request' }],
        appraisalId: parentAppraisalId, event: 'height_safety_quote_request',
      });
      const nowIso = new Date().toISOString();
      const existing = capturedData?.appraisal_record?.[stampFieldPrefix] || {};
      await api.patch(`/appraisals/${parentAppraisalId}`, {
        [stampFieldPrefix]: {
          ...existing,
          [sid]: nowIso,
        },
      });
      setSendState(prev => ({ ...prev, [sid]: { sent_at: nowIso, ok: true, email } }));
    } catch (e) {
      const errMsg = e?.response?.data?.detail || e?.message || 'Send failed';
      setSendState(prev => ({ ...prev, [sid]: { error: errMsg } }));
      setGlobalErr(errMsg);
    } finally {
      setBusy(prev => { const n = new Set(prev); n.delete(sid); return n; });
    }
  };

  const sendAll = async () => {
    for (const it of items) {
      if (isSent(it.supplier_id)) continue;
      await sendOne(it.supplier_id, it.email);
    }
  };

  useEffect(() => {
    if (onChange) onChange({ send_state: sendState });
  }, [sendState, onChange]);

  const allSent = items.length > 0 && items.every(i => isSent(i.supplier_id));
  const sentCount = items.filter(i => isSent(i.supplier_id)).length;

  return (
    <div className="space-y-4" data-testid="wf-hs-quote-request-send">
      <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
        <div className="flex items-start gap-3">
          <Mail className="w-5 h-5 mt-0.5 shrink-0 text-amber-700" />
          <div>
            <div className="text-sm font-semibold text-amber-900">Send Quote Request to Suppliers</div>
            <div className="text-sm mt-1 text-amber-800">
              {params.prompt || 'Email the quote request to each selected supplier.'}
            </div>
          </div>
        </div>
        <div className="mt-3 p-2 rounded bg-white border border-amber-200 text-[11px] text-amber-800">
          Sends a real email with the quote request letter attached.
        </div>
      </div>

      {items.length === 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
          No suppliers selected in the previous step. Go back and include at least one.
        </div>
      )}

      {items.length > 0 && (
        <>
          <div className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-slate-50">
            <div className="text-xs text-slate-600"><b className="text-slate-800">{sentCount}</b> of <b className="text-slate-800">{items.length}</b> sent</div>
            <button
              type="button"
              onClick={sendAll}
              disabled={allSent || busy.size > 0}
              className="px-3 py-1.5 rounded-md text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-40 inline-flex items-center gap-1.5"
              data-testid="wf-hs-send-all"
            >
              <Send size={12} /> {allSent ? 'All sent ✓' : (busy.size > 0 ? 'Sending…' : 'Send all')}
            </button>
          </div>

          <div className="space-y-2">
            {items.map((it) => {
              const sent = isSent(it.supplier_id);
              const isBusy = busy.has(it.supplier_id);
              const err = sendState[it.supplier_id]?.error;
              return (
                <div key={it.supplier_id} className={`p-3 border rounded-lg flex items-center justify-between gap-3 ${sent ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-slate-200'}`} data-testid={`wf-hs-send-row-${it.supplier_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-800 truncate">{it.name}</div>
                    <div className="text-[11px] text-slate-500">{it.email || 'No email on file'}</div>
                    {sent && sendState[it.supplier_id]?.sent_at && (
                      <div className="text-[10px] text-emerald-700 mt-0.5">Sent {new Date(sendState[it.supplier_id].sent_at).toLocaleString()}</div>
                    )}
                    {err && <div className="text-[10px] text-rose-700 mt-0.5">Error: {err}</div>}
                  </div>
                  <button
                    type="button"
                    onClick={() => sendOne(it.supplier_id, it.email)}
                    disabled={sent || isBusy}
                    className="px-3 py-1.5 rounded-md text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-40 inline-flex items-center gap-1.5 shrink-0"
                    data-testid={`wf-hs-send-btn-${it.supplier_id}`}
                  >
                    <Send size={12} /> {sent ? 'Sent' : (isBusy ? 'Sending…' : 'Send')}
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}

      {globalErr && (
        <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">
          <AlertCircle className="w-4 h-4 mt-0.5" /> <span>{globalErr}</span>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? (
          <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-hs-send-back">← Back</button>
        ) : <span />}
        <div className="flex gap-2">
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-hs-send-cancel">Cancel</button>}
          <button
            onClick={onNext}
            disabled={!allSent}
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="wf-hs-send-continue"
          >
            <CheckCircle2 className="w-4 h-4" /> Continue
          </button>
        </div>
      </div>
    </div>
  );
}
